import "zod/compile";
import { z } from "zod";

/**
 * The shape of a supplier's 849 STRUCTURAL rule — the deterministic tier the 849
 * classifier consults when `sd_849_structural_signals_enabled` is on.
 *
 * Lives in shared-types (moved from the backend's `classify849/structuralSignals`,
 * which re-exports it) so the supplier page's rule editor validates a rule with
 * the SAME strict schema the save uses (SD-86). The signal EXTRACTION and the
 * matching stay in the backend; only the rule vocabulary is shared.
 */

/** Any stack of reply / forward prefixes, in any order: `RE: FW: RE:`. */
const REPLY_OR_FORWARD_PREFIX = /^\s*(?:(?:re|fwd?)\s*:\s*)+/i;

/**
 * A subject's shape: reply and forward prefixes stripped, lowercased, every
 * digit run collapsed to `#`, split into word tokens. PURE.
 *
 * Digit runs collapse to `#` BEFORE tokenizing, so a claim, PO or case number
 * never makes two emails of one template look different. `&` stays a word
 * character, so "S&D" is one token rather than two meaningless letters.
 */
export function sd849SubjectShape(subject: string | null): string[] {
  return (subject ?? "")
    .replace(REPLY_OR_FORWARD_PREFIX, "")
    .toLowerCase()
    .replace(/\d+/g, "#")
    .split(/[^a-z#&]+/)
    .filter((token) => token.length > 0);
}

/**
 * A stored shape prefix as tokens, in the SAME vocabulary {@link sd849SubjectShape}
 * produces — so an operator-authored `"RE: Arrival Notice PO 88213"` reads as
 * `["arrival", "notice", "po", "#"]` and matches, instead of silently never matching
 * anything. A prefix already in shape form (every learned one) is unchanged: the
 * normalization is idempotent.
 */
export function shapePrefixTokens(prefix: string): string[] {
  return sd849SubjectShape(prefix);
}

/**
 * What a supplier's configuration states about one structural shape its 849 replies
 * take. Every field is optional and an omitted one means DON'T CARE — requiring
 * every signal to be stated would make a rule unwritable.
 *
 * Declared as Zod and inferred into the types, once, because this shape crosses a
 * trust boundary: it is operator-edited JSON saved through the supplier config
 * editor. A hand-written interface beside a separate validator is the duplication
 * the root CLAUDE.md forbids, and here it would drift silently — the only thing
 * checking the two against each other is a structural assignment.
 */
const REQUIRES_SHAPE = {
  isReply: z.boolean().optional(),
  isForward: z.boolean().optional(),
  hasVdmOrAdmToken: z.boolean().optional(),
  hasAttachment: z.boolean().optional(),
  hasSdToken: z.boolean().optional(),
  hasDebitMemoToken: z.boolean().optional(),
  hasCaseToken: z.boolean().optional(),
  hasBodyStatedDecision: z.boolean().optional(),
  /** Any one of these domains, compared case-insensitively. */
  senderDomains: z.array(z.string().min(1)).optional(),
  /**
   * The subject's shape begins with these tokens, space-separated, in the
   * vocabulary {@link sd849SubjectShape} produces (`"arrival notice po #"`).
   * Written by the classification learner (#7653); an operator may author one too,
   * in any case and with reply prefixes or real numbers — it is normalized the way
   * subjects are before it is compared ({@link shapePrefixTokens}).
   */
  subjectStartsWith: z
    .string()
    .trim()
    .min(1)
    // Refused rather than stored: a prefix with no shape token left once
    // normalized (`"RE:"`, `"--"`) can never match, and an inert rule gives no
    // signal that it is inert.
    .refine((prefix) => shapePrefixTokens(prefix).length > 0, {
      message: "subjectStartsWith has no subject-shape tokens once normalized",
    })
    .optional(),
};

/**
 * `strictObject`, not `object`. A plain `z.object` STRIPS unknown keys, so a typo
 * like `hasAttachement` silently vanishes and the rule that survives is BROADER
 * than the one authored — matching every reply with no attachment required, and
 * banding it `act`. The empty-`requires` guard below cannot catch that, because
 * the object is not empty. An unknown predicate is a mistake, and it fails loudly
 * at config-validation time instead of quietly widening what gets auto-processed.
 */
export const Sd849StructuralRuleRequiresSchema = z.strictObject(REQUIRES_SHAPE);

export const Sd849StructuralRuleSchema = z.object({
  /** Operator-chosen, and quoted in the classification's reason so a hit names its cause. */
  name: z.string().min(1),
  requires: Sd849StructuralRuleRequiresSchema,
});

export type Sd849StructuralRuleRequires = z.infer<
  typeof Sd849StructuralRuleRequiresSchema
>;
export type Sd849StructuralRule = z.infer<typeof Sd849StructuralRuleSchema>;

/**
 * Every requirement except the domain list — derived from the schema above rather
 * than restated, so a signal added there cannot be forgotten in the loop that
 * evaluates it. Indexing `Sd849StructuralSignals` by these names is what keeps the
 * two in step: a rule field the extractor does not produce fails to compile.
 */
export type Sd849BooleanSignal = Exclude<
  keyof Sd849StructuralRuleRequires,
  "senderDomains" | "subjectStartsWith"
>;

const NON_BOOLEAN_REQUIREMENTS: readonly string[] = [
  "senderDomains",
  "subjectStartsWith",
];

/** The boolean signals a rule can state, in schema order. */
export const SD849_BOOLEAN_SIGNALS = Object.keys(REQUIRES_SHAPE).filter(
  (name): name is Sd849BooleanSignal =>
    !NON_BOOLEAN_REQUIREMENTS.includes(name),
);
